import { useRef, useState } from 'react';
import isNil from 'lodash/isNil';

/**
 * Show/hide state for a secret behind an eye. With `onReveal`, the page holds only a mask and the
 * first opening fetches the real value; a failed fetch hides the secret again, so the mask is never
 * shown as if it were the value and the next click retries.
 */
const useSecretReveal = (onReveal?: () => Promise<string | null | undefined>) => {
  const [isHidden, setIsHidden] = useState(true);
  const [revealedText, setRevealedText] = useState<string | null | undefined>();
  const isRevealing = useRef(false);
  const [isAwaitingReveal, setIsAwaitingReveal] = useState(false);

  const toggleHidden = async () => {
    setIsHidden((prev) => !prev);
    if (!isHidden || !onReveal || !isNil(revealedText) || isRevealing.current) {
      return;
    }
    isRevealing.current = true;
    setIsAwaitingReveal(true);
    try {
      const revealed = await onReveal();
      setRevealedText(revealed);
      if (isNil(revealed)) {
        setIsHidden(true);
      }
    } finally {
      isRevealing.current = false;
      setIsAwaitingReveal(false);
    }
  };

  return { isMasked: isHidden || isAwaitingReveal, revealedText, toggleHidden };
};

export default useSecretReveal;
